import { useState, useEffect } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Clock, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import DatePicker from "react-datepicker";
import { subWeeks, subMonths, subYears, format } from 'date-fns';

export function DateRangeSelector({ onApply, selectedRange = null, onClear }) {
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    // Update local state when selectedRange changes
    useEffect(() => {
        if (selectedRange) {
            setDateRange([selectedRange.start instanceof Date ? selectedRange.start : new Date(selectedRange.start), selectedRange.end instanceof Date ? selectedRange.end : new Date(selectedRange.end)]);
        } else {
            setDateRange([null, null]);
        }
    }, [selectedRange]);

    const presets = [
        { label: 'Today', getValue: () => [new Date(), new Date()] },
        { label: 'Last week', getValue: () => [subWeeks(new Date(), 1), new Date()] },
        { label: 'Last month', getValue: () => [subMonths(new Date(), 1), new Date()] },
        { label: 'Last year', getValue: () => [subYears(new Date(), 1), new Date()] },
    ];

    const handlePresetClick = (range) => {
        setDateRange(range);
    };

    const handleClear = () => {
        setDateRange([null, null]);
        onClear?.();
    };

    return (
        <Popover className="relative">
            <PopoverButton 
                className={`flex items-center gap-2 px-3 py-2 ${
                    startDate ? 'bg-cyan-50 text-cyan-600' : 'bg-gray-100'
                } rounded-md hover:bg-gray-200`}
            >
                <Clock className="w-4 h-4" />
                <span>
                    {startDate && endDate
                        ? `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
                        : 'All time'}
                </span>
                <ChevronDown className="w-4 h-4" />
            </PopoverButton>

            <PopoverPanel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg border">
                <div className="flex">
                    {/* Presets */}
                    <div className="w-36 border-r p-1.5">
                        {presets.map((preset) => (
                            <button
                                key={preset.label}
                                onClick={() => handlePresetClick(preset.getValue())}
                                className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded-md"
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {/* Calendar */}
                    <div className="p-3">
                        <DatePicker
                            selected={startDate}
                            onChange={(update) => {
                                setDateRange(update);
                            }}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            inline
                            monthsShown={1}
                            showPopperArrow={false}
                            renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled
                            }) => (
                                <div className="flex items-center justify-between px-1 py-1">
                                    <span className="text-sm font-medium">
                                        {format(date, 'MMMM yyyy')}
                                    </span>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={decreaseMonth}
                                            disabled={prevMonthButtonDisabled}
                                            type="button"
                                            className="p-1 hover:bg-gray-100 rounded-md"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={increaseMonth}
                                            disabled={nextMonthButtonDisabled}
                                            type="button"
                                            className="p-1 hover:bg-gray-100 rounded-md"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-2 border-t">
                    <PopoverButton
                        as="button"
                        onClick={handleClear}
                        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                        Cancel
                    </PopoverButton>
                    <PopoverButton
                        as="button"
                        onClick={() => {
                            if (startDate && endDate) {
                                onApply?.(startDate, endDate);
                            }
                        }}
                        className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:opacity-50"
                        disabled={!startDate || !endDate}
                    >
                        Apply
                    </PopoverButton>
                </div>
            </PopoverPanel>
        </Popover>
    );
} 